import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { ContextActions } from '../components/ContextActions.jsx';
import { AddIcon, SaveIcon, DeleteIcon } from '../components/icons';

/**
 * Help articles management — tenant operators curate the quick-help
 * library that the SDK widget surfaces in its composer.
 *
 * The wire format is `{id, title, body, routes, tags}` (see RFC-026
 * §8).  Backed by tenant config (`PUT /v1/admin/config/help-articles`)
 * which replaces the whole list each save — atomic, small payload,
 * sidesteps per-article CRUD.
 *
 * UX: list on the left, editor on the right.  Pick an article to
 * edit it inline, or hit New to add one.  No drag-reorder yet;
 * order in the array is the order the widget shows them.
 */
export function HelpArticles() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const [articles, setArticles] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(null);   // editing buffer
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // Side-fetch: how many recent resolved threads exist that haven't
  // been promoted yet?  Powers the "Promote a recent reply" context
  // action — turns this page from a static editor into an active
  // "build the library from real conversations" loop.
  const [promotable, setPromotable] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, inbox, tagsResp, projectsResp] = await Promise.all([
        api('/v1/admin/config'),
        api('/v1/admin/help/requests?status=resolved').catch(() => null),
        api('/v1/admin/config/help-articles/tags').catch(() => null),
        api('/v1/admin/projects').catch(() => null),
      ]);
      setArticles(cfg?.help_articles || []);
      setTagSuggestions(tagsResp?.tags || []);
      const projectItems = projectsResp?.projects || projectsResp?.items || projectsResp || [];
      setProjects(Array.isArray(projectItems) ? projectItems : []);
      // A resolved thread with at least one operator reply is worth
      // promoting if its content isn't already an article (cheapest
      // proxy: it isn't tagged "promoted" in another article's tags).
      const resolved = inbox?.items || [];
      setPromotable(resolved.filter((r) => (r.reply_count || 0) > 0).length);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Replace the whole tenant-config list — atomic + idempotent.
  const persist = useCallback(async (next) => {
    setSaving(true);
    setError(null);
    try {
      await api('/v1/admin/config/help-articles', {
        method: 'PUT',
        body: JSON.stringify({ articles: next }),
      });
      setArticles(next);
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [api]);

  const onPick = (a) => {
    setActiveId(a.id);
    setDraft({
      ...a,
      status: a.status || 'published',
      project_id: a.project_id || '',
      routesText: (a.routes || []).join('\n'),
      tagsText: (a.tags || []).join(', '),
    });
  };

  const onNew = () => {
    const id = `article-${Date.now().toString(36).slice(-6)}`;
    const fresh = {
      id, title: 'Untitled', body: '', routes: [], tags: [],
      status: 'draft', project_id: '',
      routesText: '', tagsText: '',
    };
    setActiveId(id);
    setDraft(fresh);
  };

  const onSave = async () => {
    if (!draft) return;
    const nowIso = new Date().toISOString();
    const prev = articles.find((a) => a.id === activeId);
    const becamePublished = draft.status === 'published' && prev?.status !== 'published';
    const clean = {
      id: draft.id.trim() || `article-${Date.now().toString(36).slice(-6)}`,
      title: draft.title.trim() || 'Untitled',
      body: draft.body,
      routes: (draft.routesText || '').split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
      tags:   (draft.tagsText   || '').split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
      status: draft.status || 'published',
      project_id: draft.project_id || '',
      version: (prev?.version || 0) + (becamePublished ? 1 : 0) || 1,
      published_at: becamePublished ? nowIso : (prev?.published_at || ''),
      updated_at: nowIso,
    };
    const next = prev
      ? articles.map((a) => a.id === activeId ? clean : a)
      : [...articles, clean];
    await persist(next);
    setDraft({
      ...clean,
      routesText: clean.routes.join('\n'),
      tagsText: clean.tags.join(', '),
    });
    setActiveId(clean.id);
  };

  // Two-stage in-app confirm — click "Delete" once to arm, click
  // again within 4s to commit.  Mirrors GitHub's repo-delete pattern.
  // Replaces the previous native confirm() which the design review
  // flagged as breaking the polished feel of the editor.
  const [deleteArmed, setDeleteArmed] = useState(false);
  useEffect(() => {
    if (!deleteArmed) return undefined;
    const t = setTimeout(() => setDeleteArmed(false), 4000);
    return () => clearTimeout(t);
  }, [deleteArmed]);
  const onDelete = async () => {
    if (!draft) return;
    if (!deleteArmed) { setDeleteArmed(true); return; }
    const next = articles.filter((a) => a.id !== activeId);
    await persist(next);
    setActiveId(null);
    setDraft(null);
    setDeleteArmed(false);
  };

  const ctxActions = useMemo(() => {
    const out = [];
    if (articles.length === 0) {
      out.push({
        id: 'first-article',
        title: 'No articles yet',
        body: 'The Quick Answers panel in the widget is empty. Write your first one, or promote a great support reply from the inbox.',
        cta: '+ New article',
        onClick: onNew,
        tone: 'primary',
      });
    }
    if (promotable > 0 && articles.length > 0) {
      out.push({
        id: 'promote',
        title: promotable === 1
          ? '1 resolved thread is worth promoting'
          : `${promotable} resolved threads worth promoting`,
        body: 'Turn a reply you already wrote into a self-serve article — the next user finds it without asking.',
        cta: 'Open inbox',
        to: '/help',
        tone: 'normal',
      });
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles.length, promotable]);

  return (
    <div className="page">
      <div className="page__title">
        <div>
          <h1>Pinpoint articles</h1>
          <p className="page__subtitle">
            The quick-answers your users see in the Pinpoint widget's composer.
            Each article is matched to a page by the routes it lists; an
            article with no routes shows on every page.
            <br/>
            See conversations in the <Link to="/inbox">Inbox</Link>.
          </p>
        </div>
        <div>
          <button type="button" className="btn btn--primary" onClick={onNew}>
            <AddIcon /> New article
          </button>
        </div>
      </div>

      <ContextActions actions={ctxActions} />

      {error && (
        <div className="state state--error">
          <div className="state__title">Couldn't save</div>
          <div>{error}</div>
        </div>
      )}
      {loading && <div className="state"><div className="state__title">Loading…</div></div>}

      {!loading && (
        <div className="ha-layout">
          <aside className="ha-list">
            {articles.length === 0 && !draft && (
              <div className="ha-empty">
                <strong>No articles yet</strong>
                <p>Click <em>New article</em> to seed your first one — short answers to the questions support hears most often.</p>
              </div>
            )}
            {articles.map((a) => {
              const status = (a.status || 'published').toLowerCase();
              const projectName = projects.find((p) => (p.project_id || p.id) === a.project_id)?.name || a.project_id;
              return (
                <button
                  key={a.id}
                  type="button"
                  className={`ha-list__row${a.id === activeId ? ' ha-list__row--active' : ''}`}
                  onClick={() => onPick(a)}
                >
                  <div className="ha-list__title">
                    {a.title || a.id}
                    {status !== 'published' && (
                      <span className={`badge badge--${status === 'archived' ? 'default' : 'admin'}`} style={{ marginLeft: 6, fontSize: 10 }}>
                        {status}
                      </span>
                    )}
                  </div>
                  <div className="ha-list__meta">
                    {a.project_id ? `project: ${projectName}` : 'tenant-wide'}
                    {' · '}
                    {(a.routes && a.routes.length > 0)
                      ? <>{a.routes.length} route{a.routes.length === 1 ? '' : 's'}</>
                      : 'every page'}
                    {a.tags?.length > 0 && <> · {a.tags.join(', ')}</>}
                  </div>
                </button>
              );
            })}
          </aside>
          <section className="ha-editor card">
            {!draft ? (
              <div className="ha-editor__empty">
                <strong>Pick an article on the left</strong>
                <p>or click New to start one.  Changes save when you hit <em>Save</em> — the whole library is replaced atomically, so concurrent edits should be rare.</p>
              </div>
            ) : (
              <>
                <div className="ha-editor__head">
                  <input
                    className="ha-editor__title-input"
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    placeholder="Title — what the user clicks"
                  />
                  <div className="ha-editor__actions">
                    {savedAt && (
                      <span className="ha-editor__saved">Saved {savedAt.toLocaleTimeString()}</span>
                    )}
                    <button
                      type="button"
                      className={'btn ' + (deleteArmed ? 'btn--danger' : 'btn--secondary')}
                      onClick={onDelete}
                    >
                      <DeleteIcon /> {deleteArmed ? 'Click again to confirm' : 'Delete'}
                    </button>
                    <button type="button" className="btn btn--primary" onClick={onSave} disabled={saving}>
                      <SaveIcon /> {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
                <div className="ha-editor__fields">
                  <div className="ha-field-row">
                    <label className="ha-field">
                      <span className="ha-field__label">Status</span>
                      <select
                        className="ha-field__input"
                        value={draft.status || 'published'}
                        onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                      >
                        <option value="draft">Draft — not served to users yet</option>
                        <option value="published">Published — live in the widget</option>
                        <option value="archived">Archived — hide from widget + lists</option>
                      </select>
                    </label>
                    <label className="ha-field">
                      <span className="ha-field__label">Scope</span>
                      <select
                        className="ha-field__input"
                        value={draft.project_id || ''}
                        onChange={(e) => setDraft({ ...draft, project_id: e.target.value })}
                      >
                        <option value="">Tenant-wide (every project)</option>
                        {projects.map((p) => (
                          <option key={p.project_id || p.id} value={p.project_id || p.id}>
                            Project: {p.name || p.project_id || p.id}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <label className="ha-field">
                    <span className="ha-field__label">Body (basic markdown — **bold**, lists)</span>
                    <textarea
                      className="ha-field__textarea"
                      rows={12}
                      value={draft.body}
                      onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                      placeholder="Write the answer. Short paragraphs work best."
                    />
                  </label>
                  <div className="ha-field-row">
                    <label className="ha-field">
                      <span className="ha-field__label">Routes (one per line, empty = every page)</span>
                      <textarea
                        className="ha-field__textarea ha-field__textarea--small"
                        rows={4}
                        value={draft.routesText}
                        onChange={(e) => setDraft({ ...draft, routesText: e.target.value })}
                        placeholder="/customers&#10;/orders/*"
                      />
                    </label>
                    <label className="ha-field">
                      <span className="ha-field__label">Tags (comma-separated)</span>
                      <input
                        className="ha-field__input"
                        value={draft.tagsText}
                        onChange={(e) => setDraft({ ...draft, tagsText: e.target.value })}
                        placeholder="customers, getting-started"
                        list="ha-tag-suggestions"
                      />
                      <datalist id="ha-tag-suggestions">
                        {tagSuggestions.map((t) => <option key={t} value={t} />)}
                      </datalist>
                      {tagSuggestions.length > 0 && (
                        <div className="ha-tag-chips">
                          {tagSuggestions.slice(0, 8).map((t) => (
                            <button
                              type="button"
                              key={t}
                              className="ha-tag-chip"
                              onClick={() => {
                                const current = (draft.tagsText || '').split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
                                if (current.includes(t)) return;
                                const next = [...current, t].join(', ');
                                setDraft({ ...draft, tagsText: next });
                              }}
                            >
                              + {t}
                            </button>
                          ))}
                        </div>
                      )}
                      <span className="ha-field__id">
                        ID: <code>{draft.id}</code>
                      </span>
                    </label>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      )}

      <style>{HA_CSS}</style>
    </div>
  );
}

const HA_CSS = `
  .ha-tag-chips { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
  .ha-tag-chip { font: inherit; font-size: 11.5px; padding: 3px 8px; border-radius: 999px; border: 1px solid var(--border-1); background: var(--surface-1); cursor: pointer; color: var(--text-3); }
  .ha-tag-chip:hover { background: var(--brand-50, #eef2ff); border-color: var(--brand-600, #4f46e5); color: var(--brand-700, #4338ca); }

  .ha-layout {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 16px;
    align-items: start;
  }
  @media (max-width: 900px) {
    .ha-layout { grid-template-columns: 1fr; }
  }
  .ha-list {
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: var(--r-lg, 12px);
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 70vh;
    overflow-y: auto;
  }
  .ha-list__row {
    text-align: left;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    padding: 10px 12px;
    cursor: pointer;
    font: inherit;
    color: inherit;
    transition: background-color 0.12s ease, border-color 0.12s ease;
  }
  .ha-list__row:hover { background: var(--surface-1, #f1f5f9); }
  .ha-list__row--active {
    background: var(--brand-50, #eef2ff);
    border-color: var(--brand-600, #4f46e5);
  }
  .ha-list__title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-1, #0f172a);
    margin-bottom: 2px;
  }
  .ha-list__meta {
    font-size: 11px;
    color: var(--text-3, #64748b);
  }
  .ha-empty {
    padding: 16px 14px;
    color: var(--text-3, #64748b);
    font-size: 13px;
    text-align: center;
  }
  .ha-empty strong { display: block; color: var(--text-1); margin-bottom: 4px; }

  .ha-editor { padding: 18px 20px; }
  .ha-editor__empty {
    text-align: center;
    color: var(--text-3, #64748b);
    padding: 40px 20px;
  }
  .ha-editor__empty strong { display: block; color: var(--text-1); margin-bottom: 6px; font-size: 14px; }
  .ha-editor__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    border-bottom: 1px solid var(--border-1, #e5e7eb);
    padding-bottom: 12px;
  }
  .ha-editor__title-input {
    flex: 1;
    padding: 8px 10px;
    font-size: 15px;
    font-weight: 600;
    border: 1px solid transparent;
    border-radius: 8px;
    color: var(--text-1, #0f172a);
    background: transparent;
    outline: none;
  }
  .ha-editor__title-input:hover,
  .ha-editor__title-input:focus { border-color: var(--border-1, #cbd5e1); background: var(--surface-1, #f1f5f9); }
  .ha-editor__actions { display: flex; align-items: center; gap: 8px; }
  .ha-editor__saved { font-size: 11.5px; color: var(--text-3, #64748b); }

  .ha-field { display: flex; flex-direction: column; gap: 4px; }
  .ha-field__label { font-size: 11.5px; color: var(--text-3, #64748b); }
  .ha-field__textarea,
  .ha-field__input {
    padding: 8px 10px;
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 8px;
    font: inherit;
    font-size: 13.5px;
    line-height: 1.5;
    color: var(--text-1, #0f172a);
    background: var(--surface-0, #fff);
    box-sizing: border-box;
    width: 100%;
    resize: vertical;
  }
  .ha-field__textarea--small { font-size: 12px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
  .ha-field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-top: 12px;
  }
  @media (max-width: 700px) { .ha-field-row { grid-template-columns: 1fr; } }
  .ha-field__id { margin-top: 6px; font-size: 11px; color: var(--text-3); }
  .ha-editor__fields { display: flex; flex-direction: column; gap: 14px; }
`;
